import React, { createContext, useState, useCallback } from "react";
import { useSelector } from "react-redux";

import useDebounceEffect from "../../../../hooks/useDebounceEffect";
import useWindowResize from "../../../../hooks/useWIndowResize";

const defaultValue = {
    benefitsVisibility: Array(4).fill(null),
    benefitsRefs: Array(4).fill(null),
    loadedBanner: false,
    setLoadedBanner: loaded => {},
    setBenefitItemVisibility: (index, visible) => {},
    setBenefitItemRef: (index, ref) => {},
};

export const introSectionContext = createContext(defaultValue);

const IntroSectionProvider = ({ children }) => {
    const { activePromotionDone, activePromotion } = useSelector(state => ({
        activePromotionDone: state.HomeInsurance.activePromotionDone,
        activePromotion: state.HomeInsurance.activePromotion,
    }));
    const [context, setContext] = useState(defaultValue);
    const [resizeDone, setResizeDone] = useState(false);
    const setLoadedBanner = useCallback(
        loaded => {
            setContext({
                ...context,
                loadedBanner: loaded,
            });
        },
        [context]
    );
    const setBenefitsVisibility = useCallback(
        _benefitsVisibility => {
            setContext({
                ...context,
                benefitsVisibility: _benefitsVisibility,
            });
        },
        [context]
    );
    const setBenefitsRefs = useCallback(
        _benefitsRefs => {
            setContext({
                ...context,
                benefitsRefs: _benefitsRefs,
            });
        },
        [context]
    );
    const { benefitsVisibility, benefitsRefs } = context;
    const setBenefitItemVisibility = useCallback(
        (itemIndex, visibility) => {
            setBenefitsVisibility(
                Object.assign([], benefitsVisibility, { [itemIndex]: visibility })
            );
        },
        [benefitsVisibility, setBenefitsVisibility]
    );
    const setBenefitItemRef = useCallback(
        (itemIndex, ref) => {
            setBenefitsRefs(Object.assign([], benefitsRefs, { [itemIndex]: ref }));
        },
        [benefitsRefs, setBenefitsRefs]
    );
    const { isMobile } = useWindowResize();
    useDebounceEffect(
        () => {
            if (!activePromotionDone) {
                return;
            }
            if (resizeDone || !isMobile) return;
            if (
                benefitsVisibility.every(visible => visible !== null) &&
                benefitsVisibility.some(visible => visible)
            ) {
                if (benefitsVisibility.every(visible => visible)) {
                    setResizeDone(true);
                    return;
                }
                const lastVisibleIndex = benefitsVisibility.lastIndexOf(true);
                const firstNotVisibleIndex = benefitsVisibility.indexOf(false);
                const lastVisibleRef = benefitsRefs[lastVisibleIndex];
                const firstNotVisibleRef = benefitsRefs[firstNotVisibleIndex];
                const lastVisibleBounds = lastVisibleRef.current.getBoundingClientRect();
                const firstNotVisibleBounds = firstNotVisibleRef.current.getBoundingClientRect();
                const lastVisibleMiddle =
                    (lastVisibleBounds.bottom + lastVisibleBounds.top) / 2;
                const firstNotVisibleMiddle =
                    (firstNotVisibleBounds.bottom + firstNotVisibleBounds.top) / 2;
                const pivotBottom = window.innerHeight;
                let amountToSubstract = 0;
                if (!activePromotion) {
                    if (lastVisibleMiddle > pivotBottom) {
                        amountToSubstract = lastVisibleMiddle - pivotBottom;
                    } else {
                        amountToSubstract = firstNotVisibleMiddle - pivotBottom;
                    }
                } else {
                    const distanceToLastVisibleMiddle = lastVisibleMiddle - pivotBottom;
                    const distanceToFirstNotVisibleMiddle =
                        firstNotVisibleMiddle - pivotBottom;
                    if (
                        Math.abs(distanceToLastVisibleMiddle) <
                        Math.abs(distanceToFirstNotVisibleMiddle)
                    ) {
                        amountToSubstract = lastVisibleMiddle - pivotBottom;
                    } else {
                        amountToSubstract = firstNotVisibleMiddle - pivotBottom;
                    }
                }
                document.getElementById(
                    "landing-intro__banner-img"
                ).style.marginBottom = `-${amountToSubstract}px`;
                setResizeDone(true);
            }
        },
        [
            benefitsVisibility,
            benefitsRefs,
            resizeDone,
            context,
            isMobile,
            activePromotionDone,
        ],
        50
    );
    return (
        <introSectionContext.Provider
            value={{
                ...context,
                setLoadedBanner,
                setBenefitItemVisibility,
                setBenefitItemRef,
            }}
        >
            {children}
        </introSectionContext.Provider>
    );
};

export default React.memo(IntroSectionProvider);
